import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Chip, Tooltip } from "@mui/material";
import {
  faClock,
  faCalendar,
  faCalendarCheck,
  faCheckDouble,
  faHourglassHalf,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import {
  formatLocalDate,
  parseLocalDate,
  isDateInPast,
} from "../../utils/dateUtils";

// get time ago string
const getTimeAgo = (dateString) => {
  if (!dateString) return "";

  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return `${Math.floor(diffDays / 30)}mo ago`;
};

// get status info for a request
const getStatusInfo = (request) => {
  // inactive
  if (!request.active) {
    return { text: "Inactive", color: "default", tooltip: null };
  }

  // delivered (final stage)
  if (request.date_delivered) {
    return { text: "Delivered", color: "success", tooltip: null };
  }

  // allocated with pickup scheduled (receiver confirmed pickup)
  if (request.pickup_confirmed === 1) {
    return {
      text: "Allocated",
      color: "teal",
      tooltip: "Pickup Scheduled by Receiver",
    };
  }

  // allocated but pending confirmation (donor allocated, waiting for receiver confirmation)
  if (request.date_allocated) {
    return {
      text: "Allocated",
      color: "teal",
      tooltip: "Pickup Notice sent to Receiver",
    };
  }

  // expired (scheduled_date is in the past and not delivered)
  if (request.scheduled_date && isDateInPast(request.scheduled_date)) {
    return { text: "Expired", color: "error", tooltip: null };
  }

  // default to pending (not yet allocated)
  return { text: "Pending", color: "warning", tooltip: null };
};

// status sort comparator
const statusSortComparator = (v1, v2, param1, param2) => {
  const row1 = param1.api.getRow(param1.id);
  const row2 = param2.api.getRow(param2.id);

  const status1 = getStatusInfo(row1);
  const status2 = getStatusInfo(row2);

  // priority order: Pending (5) > Expired (4) > Allocated-pending (3) > Allocated-scheduled (2) > Delivered (1) > Inactive (0)
  const priority = {
    Pending: 5,
    Expired: 4,
    Allocated: 3, // Will be differentiated by tooltip
    Delivered: 1,
    Inactive: 0,
  };

  // for Allocated, check if pickup is scheduled (lower priority means later in sort)
  let p1 = priority[status1.text] || 0;
  let p2 = priority[status2.text] || 0;

  // Adjust priority for Allocated based on pickup_confirmed
  if (status1.text === "Allocated" && row1.pickup_confirmed) p1 = 2;
  if (status2.text === "Allocated" && row2.pickup_confirmed) p2 = 2;

  if (p1 !== p2) return p2 - p1; // descending (highest priority first)

  // if same status, sort by date_modified (most recent first)
  const date1 = new Date(row1.date_modified || 0);
  const date2 = new Date(row2.date_modified || 0);
  return date2 - date1;
};

// creates the table columns for requests
export const createRequestColumns = (t, darkMode, columnVisibility = {}) => {
  const allColumns = [
    {
      field: "id",
      headerName: t("Request #"),
      flex: 0.8,
      editable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => `#${params.value}`,
    },
    {
      field: "status",
      headerName: t("Status"),
      flex: 1,
      editable: false,
      headerAlign: "center",
      align: "center",
      sortComparator: statusSortComparator,
      renderCell: (params) => {
        const statusInfo = getStatusInfo(params.row);
        const isPickupScheduled = params.row.pickup_confirmed === 1;
        const isAllocatedStatus = statusInfo.text === "Allocated";

        // Icon selection
        const icon =
          statusInfo.text === "Delivered"
            ? faCheckDouble
            : isAllocatedStatus && isPickupScheduled
            ? faCalendarCheck
            : isAllocatedStatus
            ? faCalendar
            : statusInfo.text === "Pending"
            ? faHourglassHalf
            : statusInfo.text === "Expired"
            ? faClock
            : faTimesCircle;

        // Icon color for Allocated status - black to match Delivered
        const iconColor = isAllocatedStatus ? "#000000" : undefined;

        // Lighter green styling for Allocated badge with black text
        const allocatedSx = isAllocatedStatus
          ? {
              backgroundColor: "#4caf50",
              color: "#000",
            }
          : {};

        const chip = (
          <Chip
            icon={
              <FontAwesomeIcon
                icon={icon}
                style={
                  iconColor
                    ? { color: iconColor, fontSize: "0.65rem" }
                    : { fontSize: "0.65rem" }
                }
              />
            }
            label={t(statusInfo.text)}
            color={isAllocatedStatus ? "default" : statusInfo.color}
            size="small"
            sx={{
              fontWeight: 500,
              fontSize: "0.75rem",
              "& .MuiChip-icon": {
                fontSize: "0.65rem",
              },
              ...allocatedSx,
            }}
          />
        );

        return statusInfo.tooltip ? (
          <Tooltip title={t(statusInfo.tooltip)} placement="top">
            {chip}
          </Tooltip>
        ) : (
          chip
        );
      },
    },
    {
      field: "material",
      headerName: t("Material"),
      flex: 1.5,
      editable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "receiver_company",
      headerName: t("Requester"),
      flex: 1.5,
      editable: false,
      headerAlign: "center",
      align: "center",
      valueGetter: (value, row) => row.receiver_company || row.client_id,
    },
    {
      field: "quantity_range",
      headerName: t("Requested Qty (lbs)"),
      flex: 1.2,
      editable: false,
      headerAlign: "center",
      align: "center",
      valueGetter: (value, row) => `${row.min_qty} - ${row.max_qty}`,
      renderCell: (params) => {
        return `${params.row.min_qty} - ${params.row.max_qty}`;
      },
    },
    {
      field: "allocated_qty",
      headerName: t("Allocated Qty (lbs)"),
      flex: 1.2,
      editable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        if (params.row.date_allocated && params.row.quantity != null) {
          return (
            <Box
              sx={{
                fontWeight: 600,
                color: "var(--role-accent)",
              }}
            >
              {params.row.quantity} lbs
            </Box>
          );
        }
        return "—";
      },
    },
    {
      field: "scheduled_date",
      headerName: t("Pickup Date"),
      flex: 1.2,
      editable: false,
      type: "date",
      headerAlign: "center",
      align: "center",
      valueGetter: (value) => parseLocalDate(value),
      renderCell: (params) => formatLocalDate(params.row.scheduled_date),
    },
    {
      field: "time",
      headerName: t("Pickup Time"),
      flex: 1,
      editable: false,
      headerAlign: "center",
      align: "center",
      valueGetter: (value) => value || "N/A",
    },
    {
      field: "allocated_cost",
      headerName: t("Final Revenue"),
      flex: 0.8,
      editable: false,
      type: "number",
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        if (params.row.date_allocated && params.value != null) {
          return (
            <Box
              sx={{
                fontWeight: 600,
                color: "var(--role-accent)",
              }}
            >
              ${Number(params.value).toFixed(2)}
            </Box>
          );
        }
        return "—";
      },
    },
    {
      field: "date_modified",
      headerName: t("Request Made"),
      flex: 1,
      editable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const timeAgo = getTimeAgo(params.value);
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              fontSize: "0.813rem",
            }}
          >
            <FontAwesomeIcon icon={faClock} style={{ fontSize: "0.75rem" }} />
            {timeAgo}
          </Box>
        );
      },
    },
    {
      field: "listing_id",
      headerName: t("Listing #"),
      flex: 0.8,
      editable: false,
      headerAlign: "center",
      align: "center",
    },
  ];

  // filter columns based on visibility
  return allColumns.filter(
    (column) => columnVisibility[column.field] !== false
  );
};
